/**
 * hy-consent.js (M6 AP-92): Brücke zwischen der Shopify Customer Privacy
 * API und Google Consent Mode v2. Single source of truth für alle
 * theme-seitigen Tags — kein Skript feuert vor Einwilligung (CLAUDE.md
 * Regel 10).
 *
 * Ablauf:
 *   1. window.HY.consent anlegen (kanonischer Erstinitialisierer des
 *      Namensraums window.HY — CLAUDE.md JS: keine globalen Variablen
 *      außer window.HY).
 *   2. Shopify.loadFeatures(['consent-tracking-api'@0.1]) laden.
 *   3. Im Callback SOFORT den gespeicherten Consent lesen und ein erstes
 *      gtag('consent','update',…) senden — deckt Rückkehrer ab, deren Wahl
 *      schon feststeht, bevor der wait_for_update-Timer im Consent-default
 *      abläuft.
 *   4. visitorConsentCollected abonnieren für spätere Änderungen.
 *
 * Signal-Mapping: analytics → analytics_storage; marketing → ad_storage +
 * ad_user_data + ad_personalization (docs/tracking.md § 10.2).
 *
 * window.HY.consent-Contract:
 *   allowed('analytics'|'marketing'|'preferences') → boolean (deny-by-default)
 *   analytics / marketing / preferences / saleOfData → boolean (letzter Stand)
 *   ready → Promise<void>, resolved nach dem ersten Consent-Read
 * CustomEvent hy:consent:updated auf document,
 *   detail = { analytics, marketing, preferences, sale_of_data }.
 *
 * Fehlt window.Shopify (Editor-Preview headless, kein Storefront): der
 * Contract wird trotzdem angelegt, allowed() liefert immer false, ready
 * resolved sofort, kein gtag-Update, kein Event, kein Throw.
 *
 * Footer-Button [data-hy-consent-preferences] (nur bei tracking_cmp ==
 * 'shopify', mit hidden gerendert): Handler wird sofort gebunden, das
 * Sichtbarmachen (hidden entfernen) passiert ERST, wenn
 * window.privacyBanner.showPreferences als Funktion bestätigt ist —
 * sonst gäbe es einen sichtbaren, folgenlosen Button, falls Shopifys
 * Cookie-Banner im Shop nicht aktiviert ist (reviewer-Befund PR E #5).
 *
 * Musterausnahme (CLAUDE.md JS): kein Custom Element, kein
 * customElements.define / disconnectedCallback. Dies ist ein
 * Seiten-Lebensdauer-Bootstrap, keine Komponente — die document-Listener
 * und privacy.subscribe leben bewusst für die Seitendauer (docs/tracking.md
 * § 10, ADR-11-Entwurf).
 */

window.HY = window.HY || {};

if (!window.HY.consent) {
  const api = {
    analytics: false,
    marketing: false,
    preferences: false,
    saleOfData: false,
    allowed(category) {
      if (category === 'analytics') return api.analytics;
      if (category === 'marketing') return api.marketing;
      if (category === 'preferences') return api.preferences;
      return false;
    },
    ready: null,
  };
  window.HY.consent = api;
}

const consent = window.HY.consent;

let resolveReady;
consent.ready = new Promise((resolve) => {
  resolveReady = resolve;
});

function grant(allowed) {
  return allowed ? 'granted' : 'denied';
}

function sendConsentUpdate() {
  if (typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: grant(consent.analytics),
    ad_storage: grant(consent.marketing),
    ad_user_data: grant(consent.marketing),
    ad_personalization: grant(consent.marketing),
  });
}

function readAndApply(privacy) {
  const read = (fn) => {
    try {
      return typeof privacy[fn] === 'function' && privacy[fn]() === true;
    } catch (error) {
      return false;
    }
  };

  consent.analytics = read('analyticsProcessingAllowed');
  consent.marketing = read('marketingAllowed');
  consent.preferences = read('preferencesProcessingAllowed');
  consent.saleOfData = read('saleOfDataAllowed');

  sendConsentUpdate();

  document.dispatchEvent(
    new CustomEvent('hy:consent:updated', {
      detail: {
        analytics: consent.analytics,
        marketing: consent.marketing,
        preferences: consent.preferences,
        sale_of_data: consent.saleOfData,
      },
    }),
  );
}

function hasPrivacyBanner() {
  return !!(
    window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function'
  );
}

let preferencesButton = null;
let preferencesWrapper = null;

function bindPreferencesButton() {
  preferencesButton = document.querySelector('[data-hy-consent-preferences]');
  if (!preferencesButton) return;
  preferencesWrapper = preferencesButton.closest('.hy-footer__consent') || preferencesButton;
  preferencesButton.addEventListener('click', () => {
    if (hasPrivacyBanner()) window.privacyBanner.showPreferences();
  });
}

// hidden sitzt am Wrapper (kein leerer Flex-Slot ohne Banner). Wird sowohl im
// loadFeatures-Callback als auch beim ersten visitorConsentCollected versucht —
// window.privacyBanner ist im Callback nicht garantiert schon gesetzt
// (reviewer-Befund PR E Runde 2 #1/#2).
function revealPreferencesButton() {
  if (preferencesWrapper && hasPrivacyBanner()) preferencesWrapper.hidden = false;
}

function init() {
  bindPreferencesButton();

  if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
    // Kein Storefront-Kontext: deny-by-default, kein Fehler.
    resolveReady();
    return;
  }

  window.Shopify.loadFeatures(
    [{ name: 'consent-tracking-api', version: '0.1' }],
    (error) => {
      const privacy = window.Shopify.customerPrivacy;
      if (error || !privacy) {
        resolveReady();
        return;
      }

      revealPreferencesButton();
      readAndApply(privacy);
      resolveReady();

      if (typeof privacy.subscribe === 'function') {
        privacy.subscribe('visitorConsentCollected', () => {
          revealPreferencesButton();
          readAndApply(privacy);
        });
      }
    },
  );
}

// type="module" defer: läuft nach dem Parsen, der Footer steht dann im DOM.
init();
